/**
 * Approval History — Searchable audit trail of all past decisions
 *
 * Features:
 * - Search across action/agent/tool
 * - Filter by status, type, date range
 * - Decided-by column
 * - Clickable rows navigate to detail page
 * - Export button (CSV)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, CheckCircle, XCircle, Clock, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { AgentIcon } from '@/components/shared/icon-picker';
import { useTrustUsers, shortId } from '@/lib/use-trust-entities';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  APPROVAL_TYPE_LABELS,
  APPROVAL_LEVEL_STYLES,
  APPROVAL_STATUS_STYLES,
  type ApprovalRequest,
  type ApprovalStatus,
  type ApprovalType,
} from '@/lib/mock-approvals-data';
import { useHistoryApprovals } from '@/lib/use-agent-approvals';

export const Route = createFileRoute('/approvals/history')({
  component: ApprovalHistoryPage,
});

function ApprovalHistoryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ApprovalStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | ApprovalType>('all');

  // Real-data: agent_tool_approvals where status != pending.
  const { data: allHistory = [] } = useHistoryApprovals();

  // Real-data: resolve decidedBy UUIDs to display names.
  const { data: users = [] } = useTrustUsers();
  const userById = new Map(users.map(u => [u.id, u]));
  const formatDecidedBy = (id: string | null | undefined): string => {
    if (!id) return '—';
    const u = userById.get(id);
    return u?.name || u?.email || `user:${shortId(id)}`;
  };

  const filtered = allHistory.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return a.action.title.toLowerCase().includes(q) || a.agent.name.toLowerCase().includes(q) || a.toolDisplayName.toLowerCase().includes(q);
  });

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'requestedAt', 'desc');

  const handleExport = () => {
    const rows = sorted.map(a => [
      a.action.title,
      a.type,
      a.level,
      a.status,
      a.priority,
      a.agent.name,
      a.requestedBy,
      a.requestedAt,
      formatDecidedBy(a.decidedBy),
      a.decidedAt ?? '',
      a.decisionReason ?? '',
    ]);
    const header = 'Title,Type,Level,Status,Priority,Agent,Requested By,Requested At,Decided By,Decided At,Reason';
    const csv = [header, ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `approval-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported approval history as CSV');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search history..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as 'all' | ApprovalStatus)}>
          <SelectTrigger className="w-32"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="edited">Edited</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as 'all' | ApprovalType)}>
          <SelectTrigger className="w-32"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(APPROVAL_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground">{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
        <Button variant="outline" size="sm" className="gap-1.5 ml-auto" title="Export as CSV" onClick={handleExport}>
          <Download className="h-3.5 w-3.5" /> Export
        </Button>
      </div>

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[minmax(250px,2fr)_80px_90px_80px_100px_100px_90px_1fr] gap-3 p-3 bg-muted/50 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              <SortableHeader label="Action" sortKey="action.title" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Level" sortKey="level" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Requested" sortKey="requestedAt" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Decided" sortKey="decidedAt" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Decided By" sortKey="decidedBy" currentSort={sortConfig} onSort={handleSort} />
              <span>Reason</span>
            </div>
            <div className="divide-y">
              {sorted.map(item => (
                <div
                  key={item.id}
                  className="grid grid-cols-[minmax(250px,2fr)_80px_90px_80px_100px_100px_90px_1fr] gap-3 p-3 items-center hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => navigate({ to: '/approvals/$approvalId', params: { approvalId: item.id } })}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <AgentIcon iconName={item.agent.icon} colorName={item.agent.iconColor} size="sm" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{item.action.title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{item.agent.name} · {item.toolDisplayName}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={cn('text-[9px] w-fit', APPROVAL_TYPE_LABELS[item.type].color)}>
                    {APPROVAL_TYPE_LABELS[item.type].label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[9px] w-fit', APPROVAL_LEVEL_STYLES[item.level].color)}>
                    {APPROVAL_LEVEL_STYLES[item.level].label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[9px] w-fit', APPROVAL_STATUS_STYLES[item.status])}>
                    {item.status === 'approved' && <CheckCircle className="h-2.5 w-2.5 mr-0.5" />}
                    {item.status === 'rejected' && <XCircle className="h-2.5 w-2.5 mr-0.5" />}
                    {item.status}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{new Date(item.requestedAt).toLocaleDateString()}</span>
                  <span className="text-[10px] text-muted-foreground">{item.decidedAt ? new Date(item.decidedAt).toLocaleDateString() : '—'}</span>
                  <span className="text-[10px] text-muted-foreground truncate" title={item.decidedBy ?? ''}>{formatDecidedBy(item.decidedBy)}</span>
                  <p className="text-[10px] text-muted-foreground truncate" title={item.decisionReason}>{item.decisionReason || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Clock className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No history matching your filters</p>
        </div>
      )}
    </div>
  );
}
